/**
 * Pure formatting functions for job card labels.
 * Used by SSR to precompute labels so cards don't run Intl/Date/toLocaleString in render.
 * All logic is isolated and tested for consistency and edge cases.
 */

import { formatSalaryMinMaxPair, formatSalaryMinMaxParts } from './format-salary-min-max-pair';

/**
 * Formats "last seen" as a relative time string (e.g. "2 days ago") using Intl.RelativeTimeFormat.
 * Safe for SSR when called with a fixed reference time (e.g. request time).
 *
 * @param lastSeen - ISO date string or Unix timestamp (ms)
 * @param locale - BCP 47 locale (e.g. 'en', 'fi', 'sv')
 * @param nowMs - Reference time in ms (default: Date.now(); pass explicitly for SSR/test)
 * @returns Localized relative time string, or empty string if invalid
 */
export function formatLastSeenLabel(
  lastSeen: number | string | undefined,
  locale: string,
  nowMs?: number
): string {
  if (lastSeen === undefined || lastSeen === null) return '';
  const now = typeof nowMs === 'number' && Number.isFinite(nowMs) ? nowMs : Date.now();
  const parseTimestamp = (value: number | string): Date => {
    if (typeof value === 'number') {
      return new Date(value < 10_000_000_000 ? value * 1000 : value);
    }
    const raw = String(value).trim();
    if (/^\d+$/.test(raw)) {
      const numeric = Number(raw);
      if (Number.isFinite(numeric)) {
        return new Date(numeric < 10_000_000_000 ? numeric * 1000 : numeric);
      }
    }
    return new Date(raw);
  };
  const date = parseTimestamp(lastSeen);
  if (Number.isNaN(date.getTime())) return '';
  const diffMs = now - date.getTime();
  if (diffMs < 0) return ''; // future date

  const diffMins = Math.floor(diffMs / 60_000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  if (diffMins < 120) {
    return rtf.format(-diffMins, 'minute');
  }
  if (diffHours < 24) {
    return rtf.format(-diffHours, 'hour');
  }
  return rtf.format(-diffDays, 'day');
}

/**
 * Formats a salary range for display (e.g. "€4,000 - €6,000", or "€4,000" when min === max).
 * Uses Intl.NumberFormat so output matches toLocaleString() and is locale-aware.
 *
 * @param min - Minimum salary (optional)
 * @param max - Maximum salary (optional)
 * @param locale - BCP 47 locale for number formatting (e.g. 'en', 'fi', 'sv')
 * @returns Formatted range string, or empty string if no valid range (caller shows "Salary not specified")
 */
export function formatSalaryLabel(
  min: number | undefined,
  max: number | undefined,
  locale?: string
): string {
  const hasMin = typeof min === 'number' && Number.isFinite(min) && min > 0;
  const hasMax = typeof max === 'number' && Number.isFinite(max) && max > 0;
  if (!hasMin || !hasMax) return '';
  const loc = locale || 'en';
  const nf = new Intl.NumberFormat(loc);
  const formatAmount = (n: number) => `€${nf.format(n)}`;
  return formatSalaryMinMaxPair(min, max, formatAmount);
}

/**
 * Salary amounts for the SRP job card's brand panel, split so each figure can be rendered
 * with `white-space: nowrap` (see formatSalaryMinMaxParts). Unlike formatSrpSalaryDisplay,
 * this never prepends "Estimated"/"Arvioitu"/"Uppskattat" — the card shows that separately
 * via its existing "Monthly" / "Est. Monthly" caption, so baking it into the figure too was
 * saying the same thing twice.
 *
 * @returns [] when there's no valid range (caller shows "Salary not specified"), one amount
 * when min === max, otherwise [min, max].
 */
export function formatSrpSalaryParts(args: {
  salaryRangeMin?: number | null;
  salaryRangeMax?: number | null;
  locale?: string;
}): string[] {
  const min = args.salaryRangeMin ?? undefined;
  const max = args.salaryRangeMax ?? undefined;
  const hasMin = typeof min === 'number' && Number.isFinite(min) && min > 0;
  const hasMax = typeof max === 'number' && Number.isFinite(max) && max > 0;
  if (!hasMin || !hasMax) return [];
  const nf = new Intl.NumberFormat(args.locale || 'en');
  const formatAmount = (n: number) => `€${nf.format(n)}`;
  return formatSalaryMinMaxParts(min, max, formatAmount);
}

function estimatePrefixForSrp(locale: string | undefined): string {
  const l = (locale ?? 'en').toLowerCase();
  if (l.startsWith('fi')) return 'Arvioitu';
  if (l.startsWith('sv')) return 'Uppskattat';
  return 'Estimated';
}

/**
 * Salary line for search-result cards (SRP, map pills, etc.).
 * When pay is filled from aggregated market stats (`salaryIsEstimate`), prefix the range
 * so it is not mistaken for an employer-stated salary.
 */
export function formatSrpSalaryDisplay(args: {
  salaryRangeMin?: number | null;
  salaryRangeMax?: number | null;
  salaryIsEstimate?: boolean | null;
  locale?: string;
}): string {
  const range = formatSalaryLabel(
    args.salaryRangeMin ?? undefined,
    args.salaryRangeMax ?? undefined,
    args.locale,
  );
  if (!range) return '';
  if (args.salaryIsEstimate === true) {
    return `${estimatePrefixForSrp(args.locale)} ${range}`;
  }
  return range;
}

/**
 * Enriches an array of hit objects with precomputed _lastSeenLabel and _salaryLabel.
 * Used by SSR so job cards can render without running Intl/Date in the browser.
 *
 * @param hits - Raw Algolia hits (mutated in place with added fields)
 * @param locale - BCP 47 locale
 * @param nowMs - Reference time in ms (e.g. Date.now() at request start)
 */
export function enrichHitsWithCardLabels(
  hits: Array<Record<string, unknown>>,
  locale: string,
  nowMs: number
): void {
  for (const hit of hits) {
    const lastSeen = hit.lastSeen as number | string | undefined;
    const min = hit.salaryRangeMin as number | undefined;
    const max = hit.salaryRangeMax as number | undefined;
    const salaryIsEstimate = hit.salaryIsEstimate as boolean | undefined;
    const lastSeenLabel = formatLastSeenLabel(lastSeen, locale, nowMs);
    const salaryLabel = formatSrpSalaryDisplay({
      salaryRangeMin: min,
      salaryRangeMax: max,
      salaryIsEstimate,
      locale,
    });
    if (lastSeenLabel) (hit as Record<string, unknown>)._lastSeenLabel = lastSeenLabel;
    if (salaryLabel) (hit as Record<string, unknown>)._salaryLabel = salaryLabel;
  }
}
